import { UseFormReturn } from 'react-hook-form';
import { useIntl } from 'react-intl';

import InputFormField from 'src/registry/components/forms/InputFormField';
import { Form } from 'src/registry/ui/Form';
import { MoneyAccountFormSchemaValues } from 'src/features/moneyAccounts/utils/formSchema';
import { commonMessages } from 'src/utils/messages';
import { moneyAccountsMessages } from 'src/features/moneyAccounts/utils/messages';
import SelectFormField from 'src/registry/components/forms/SelectFormField';
import { MONEY_ACCOUNT_TYPE } from 'src/features/moneyAccounts/utils/types';
import {
    BOOLEAN_MAP,
    BOOLEAN_OPTIONS,
    CURRENCY_MAP,
    MONEY_ACCOUNT_TYPE_TRANSLATION_MAP,
} from 'src/features/moneyAccounts/utils/constants';
import { CURRENCY } from 'src/types';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import { billingMessages } from 'src/features/billing/utils/messages';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useListBillingEntities } from 'src/features/billing/api/useListBillingEntities';

type MoneyAccountFormProps = {
    form: UseFormReturn<MoneyAccountFormSchemaValues>;
    onSubmit: (values: MoneyAccountFormSchemaValues) => void;
};

function MoneyAccountForm({ onSubmit, form }: MoneyAccountFormProps) {
    const { formatMessage } = useIntl();

    const { type } = form.watch();
    const { entityId } = useParams();

    const onBillingEntityDashboard = Boolean(entityId);

    const [searchBillingEntity, setSearchBillingEntity] = React.useState<string>('');

    const { data: billingEntities } = useListBillingEntities({
        search: searchBillingEntity,
    });

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
            <InputFormField<MoneyAccountFormSchemaValues>
                form={form}
                name="name"
                label={formatMessage(commonMessages.name)}
            />
            <SelectFormField<MoneyAccountFormSchemaValues>
                form={form}
                name="type"
                required
                label={formatMessage(moneyAccountsMessages.type)}
                options={Object.values(MONEY_ACCOUNT_TYPE)}
                optionTranslationMap={MONEY_ACCOUNT_TYPE_TRANSLATION_MAP}
            />
            <SelectFormField<MoneyAccountFormSchemaValues>
                form={form}
                name="currency"
                required
                label={formatMessage(commonMessages.currency)}
                options={Object.values(CURRENCY)}
                optionTranslationMap={CURRENCY_MAP}
            />
            <InputFormField<MoneyAccountFormSchemaValues>
                form={form}
                name="reference"
                label={formatMessage(moneyAccountsMessages.reference)}
            />
            <InputFormField<MoneyAccountFormSchemaValues>
                form={form}
                name="iban"
                required={type === MONEY_ACCOUNT_TYPE.BANK}
                label={formatMessage(moneyAccountsMessages.iban)}
            />
            <SearchSelectFormField
                name="billing_entity"
                control={form.control}
                label={formatMessage(billingMessages.billingEntity)}
                items={billingEntities?.results ?? []}
                clearable
                placeholder={`${formatMessage(billingMessages.billingEntity)}...`}
                searchPlaceholder={formatMessage(billingMessages.searchBillingEntity)}
                emptyTitle={formatMessage(billingMessages.noBillingEntity)}
                emptyDescription={formatMessage(billingMessages.noBillingEntityFound)}
                getItemValue={(entity) => entity.id}
                getItemLabel={(entity) => entity.name}
                getItemKeywords={(entity) => [entity.name]}
                loadOptions={setSearchBillingEntity}
                disabled={onBillingEntityDashboard}
            />
            <SelectFormField<MoneyAccountFormSchemaValues>
                form={form}
                name="is_active"
                isBoolean={true}
                label={formatMessage(moneyAccountsMessages.isActive)}
                options={Object.values(BOOLEAN_OPTIONS)}
                optionTranslationMap={BOOLEAN_MAP}
            />
        </Form>
    );
}

export default MoneyAccountForm;
